/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import moment from "moment";
import WeekController from "./Week/WeekController";
import {
  calendarSelectedDayKey,
  calendarSelectedViewKey,
} from "../../util/LocalStorageVariables";
import CalendarDayNewReservationModal from "./NewReservation/CalendarDayNewReservationModal";
import CalendarWeekNewShiftModal from "./Week/CalendarWeekNewShiftModal";
import YearView from "./Year/YearView";
import { useUser } from "../../contexts/UserContext";
import DayDataController from "./Day/NewView/DayDataController";
import {
  defaultCalendarEndTime,
  defaultCalendarStartTime,
  momentDateFormat,
  momentTimeFormat,
} from "./calendarConfig";
import { TodayShift } from "../../models/calendar/TodayShift";
import { useClinic } from "../../contexts/ClinicContext";
import Navigation from "./Navigation/Navigation";
import NavigationMobile from "./Navigation/NavigationMobile";

export enum ViewTypes {
  DAY = "DAY",
  DOCTOR_DAY = "DOCTOR_DAY",
  WEEK = "WEEK",
  YEAR = "YEAR",
  LISTVIEW = "LISTVIEW",
  SHIFTLISTVIEW = "SHIFTLISTVIEW",
}

const MainCalendar: React.FC = () => {
  const { clinic } = useClinic();
  const { user } = useUser();

  const [selectedDay, setSelectedDay] = useState(
    sessionStorage.getItem(calendarSelectedDayKey) ||
      moment().format(momentDateFormat)
  );
  const [weekEnd, setWeekEnd] = useState<boolean>(false);
  const [viewType, setViewType] = useState<ViewTypes>();
  const [calendarStartTime, setCalendarStartTime] = useState<moment.Moment>(
    moment(defaultCalendarStartTime, momentTimeFormat)
  );
  const [calendarEndTime, setCalendarEndTime] = useState<moment.Moment>(
    moment(defaultCalendarEndTime, momentTimeFormat)
  );
  const [reload, setReload] = useState<boolean>(false);


  const [modalNewReservationIsOpen, setModalNewReservationIsOpen] =
    useState<boolean>(false);
  const [modalNewShiftIsOpen, setModalNewShiftIsOpen] =
    useState<boolean>(false);
  const [selectedTimeAndDate, setSelectedTimeAndDate] = useState<Date>(
    new Date()
  );
  const [selectedCollaborator, setSelectedCollaborator] =
    useState<TodayShift>();
  const [isEmergency, setIsEmergency] = useState(false);
  const now = moment();
  const [refreshReservationInProgress, setRefreshReservationInProgress] =
    useState<boolean>(false);

  const openNewReservationModal = (timeAndDateToReserve?: Date) => {
    setSelectedTimeAndDate(timeAndDateToReserve || new Date());
    setModalNewReservationIsOpen(true);
  };

  const closeNewReservationModal = () => {
    if (isEmergency) {
      setIsEmergency(false);
    }
    setModalNewReservationIsOpen(false);
  };

  const openNewShiftModal = () => {
    setModalNewShiftIsOpen(true);
  };

  const closeNewShiftModal = () => {
    setModalNewShiftIsOpen(false);
  };
  useEffect(() => {
    const setDefaultViewType = () => {
      const isUserVet =
      clinic?.vets?.find((i) => i.userId === user?.userId) !== undefined;
      
      const screenwidth = window.innerWidth;

      let defaultViewType = ViewTypes.DAY;

      if(screenwidth > 1023){
        if(isUserVet){
          defaultViewType = ViewTypes.DOCTOR_DAY;
        }
      } else {
        defaultViewType = ViewTypes.LISTVIEW;
        setWeekEnd(true);
      }

      const savedViewType = sessionStorage.getItem(
        calendarSelectedViewKey
      ) as ViewTypes;

      if(savedViewType) {
        setViewType(savedViewType);
        return;
      }

      setViewType(savedViewType || defaultViewType);
    }

    if (clinic?.calendarDayStartTime) {
      setCalendarStartTime(moment(clinic.calendarDayStartTime, "hh:mm"));
    }
    if (clinic?.calendarDayEndTime) {
      setCalendarEndTime(moment(clinic.calendarDayEndTime, "hh:mm"));
    }

    setDefaultViewType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic]);

  useEffect(() => {
    sessionStorage.setItem(calendarSelectedDayKey, selectedDay);
  }, [selectedDay]);

  useEffect(() => {
    if (viewType) {
      sessionStorage.setItem(calendarSelectedViewKey, viewType);
    }
  }, [viewType]);

  return (
    <main className="main-signed-in-appointments">
      <section>
        <div className="relative z-10 border-b w-full flex flex-col bg-white dark:bg-gray-800 dark:border-b dark:border-gray-700 px-4 lg:px-6 pt-4 lg:pt-6 pb-4">
          <div className="hidden lg:flex lg:flex-col space-y-3 md:space-y-6 ">
            <Navigation
              setSelectedDay={setSelectedDay}
              setIsEmergency={setIsEmergency}
              selectedDay={selectedDay}
              openNewReservationModal={openNewReservationModal}
              openNewShiftModal={openNewShiftModal}
               setViewType={setViewType}
              viewType={viewType}
              setWeekEnd={setWeekEnd}
              weekEnd={weekEnd}
            />
          </div>
          <div className="lg:hidden space-y-3 md:space-y-6 ">
            <NavigationMobile 
              setSelectedDay={setSelectedDay}
              setIsEmergency={setIsEmergency}
              selectedDay={selectedDay}
              openNewReservationModal={openNewReservationModal}
              openNewShiftModal={openNewShiftModal}
              setViewType={setViewType}
              viewType={viewType}
            />
          </div>
        </div>
      </section>
      <section>
        <div>
          {
            viewType === ViewTypes.WEEK ||
            viewType === ViewTypes.SHIFTLISTVIEW ?
            <WeekController
              selectedDay={selectedDay}
              calendarStartTime={calendarStartTime}
              calendarEndTime={calendarEndTime}
              setSelectedDay={(newDate: string) => {
                setSelectedDay(newDate);
              }}
              triggerReload={reload}
              viewType={viewType || ViewTypes.DAY}
              weekEnd={weekEnd}
            />
            :
            <></>
          }
          {
            viewType === ViewTypes.YEAR ?
            <YearView
              selectedDay={selectedDay}
              setSelectedDay={(newDate: string) => {
                setSelectedDay(newDate);
              }}
            />
            :
            <></>
          }
          { 
            viewType === ViewTypes.DAY ||
            viewType === ViewTypes.LISTVIEW ||
            viewType === ViewTypes.DOCTOR_DAY ? (
            <DayDataController
              selectedDay={selectedDay}
              calendarStartTime={calendarStartTime}
              calendarEndTime={calendarEndTime}
              handleNewReservationModal={(
                open: boolean,
                dateAndTime?: { time: string; date: string },
                selectedShift?: TodayShift
              ) => {
                setModalNewReservationIsOpen(open);
                if (dateAndTime) {
                  setSelectedTimeAndDate(
                    moment(`${dateAndTime.date} ${dateAndTime.time}`).toDate()
                  );
                }
                if (selectedShift) {
                  setSelectedCollaborator(selectedShift);
                }
              }}
              refreshReservationInProgress={refreshReservationInProgress}
              triggerReload={reload}
              viewType={viewType || ViewTypes.DAY}
            />
          ) : (
            <></>
          )}
          <CalendarDayNewReservationModal
            close={closeNewReservationModal}
            dateAndTime={selectedTimeAndDate}
            isEmergency={isEmergency}
            open={modalNewReservationIsOpen}
            refreshReservationInProgress={() => {
              setRefreshReservationInProgress(!refreshReservationInProgress);
            }}
            reload={() => {
              setReload(!reload);
            }}
            selectedShift={selectedCollaborator}
          />
          <CalendarWeekNewShiftModal
            open={modalNewShiftIsOpen}
            closeNewModal={closeNewShiftModal}
            selectedTimeAndDate={{
              date: now.format(momentDateFormat),
              time: now.format(momentTimeFormat),
            }}
            reload={() => {
              setReload(!reload);
            }}
          />
        </div>
      </section>
    </main>
  );
};

export default MainCalendar;
